import { createZodFormResolver } from '@mews/framework';
import { SteppedDialogFormProps, useSteppedDialogFormControls } from '@mews-commander/form';
import { WithAccountId } from '@mews-commander/types';
import { useCreatePaymentPlan, useGetPaymentPlansSuspense } from '../../tables/PaymentPlans/queries';
import { createPaymentPlanFormDetailsSchema, createPaymentPlanFormSchema, CreatePaymentPlanFormValues } from './schema';
import { CreatePaymentPlanFormField, CreatePaymentPlanFormStep, PaymentPlanIntervalKind } from './types';
import { preparePaymentPlanCreateParameters } from './utils';
import { useGetReservationsForCreatePaymentPlanSuspense } from './hooks';
import { CreatePaymentPlanDialogFormContent } from './CreatePaymentPlanDialogFormContent';

export const CreatePaymentPlanDialogSteppedForm = ({
    accountId,
    onSuccess,
    onSubmitting,
    onSubmitted,
    ...props
}: SteppedDialogFormProps<CreatePaymentPlanFormStep> & WithAccountId) => {
    const reservations = useGetReservationsForCreatePaymentPlanSuspense({ accountId });
    const {
        data: { paymentPlans },
    } = useGetPaymentPlansSuspense({ customerId: accountId });

    const paymentPlanFormSchema = createPaymentPlanFormSchema(reservations, paymentPlans ?? []);
    const paymentPlanFormResolver = createZodFormResolver(paymentPlanFormSchema);

    const paymentPlanDetailsSchema = createPaymentPlanFormDetailsSchema(reservations, paymentPlans ?? []);
    const paymentPlanDetailsFormResolver = createZodFormResolver(paymentPlanDetailsSchema);

    const { mutate } = useCreatePaymentPlan({ accountId });

    const { renderFormContent } = useSteppedDialogFormControls<CreatePaymentPlanFormStep, CreatePaymentPlanFormValues>({
        ...props,
        onSubmit: (formValues) => {
            onSubmitting?.();
            mutate(preparePaymentPlanCreateParameters({ accountId, formValues }), {
                onSettled: onSubmitted,
                onSuccess,
            });
        },
        stepResolvers: {
            planDetails: paymentPlanDetailsFormResolver,
            firstPayment: paymentPlanFormResolver,
        },
        defaultValues: {
            [CreatePaymentPlanFormField.PaymentPlanIntervalKind]: PaymentPlanIntervalKind.Reservation,
        },
    });

    return renderFormContent((step) => {
        return <CreatePaymentPlanDialogFormContent step={step} accountId={accountId} />;
    });
};
